import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  Building2, CreditCard, Phone, Mail, Globe, MapPin,
  Briefcase, CalendarDays, ShoppingCart, Tag, Keyboard, Save, Home
} from 'lucide-react';
import AddressAutocomplete from '../AddressAutocomplete';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC'
];

function RadioGroup({ label, options, value, onChange, required, testIdPrefix }) {
  return (
    <div>
      <Label className="mb-2 block text-sm font-medium">{label}{required && ' *'}</Label>
      <div className="flex flex-wrap gap-3">
        {options.map(opt => (
          <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name={testIdPrefix}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="w-4 h-4 accent-[#3b82f6]"
              data-testid={`${testIdPrefix}-${opt.value.toLowerCase().replace(/\s+/g, '-')}`}
            />
            <span className="text-sm">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Step1BusinessInfo({ application, onNext, saving, onSaveDraft }) {
  const section = application.sections?.find(s => s.step_number === 1) || {};
  const [formData, setFormData] = useState({
    legal_business_name: '',
    dba_same_as_corporate: 'yes',
    dba_name: '',
    dba_address: '',
    dba_city: '',
    dba_state: '',
    dba_zip: '',
    federal_tax_id: '',
    business_phone: '',
    business_email: '',
    website: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    business_suite: '',
    business_type: '',
    date_established: '',
    goods_services_sold: '',
    mcc_code: '',
    keyed_transactions_pct: '',
    // Business Location (required by Maverick/TSYS)
    building_type: '',
    building_ownership: '',
    area_zoned: '',
    square_footage: '',
    ...section.data
  });

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step1-title">Business Information</h3>
          <p className="text-sm text-muted-foreground">Please provide your business details</p>
        </div>
      </div>

      <div className="space-y-5">
        {/* Row 1: Legal Name + Entity Type */}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Building2 className="h-3.5 w-3.5 text-[#3b82f6]" /> Corporate / Legal Name *
            </Label>
            <Input value={formData.legal_business_name} onChange={(e) => set('legal_business_name', e.target.value)}
              placeholder="Enter legal/corporate name" required data-testid="legal-business-name-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Briefcase className="h-3.5 w-3.5 text-[#3b82f6]" /> Ownership / Entity Type *
            </Label>
            <Select value={formData.business_type} onValueChange={(v) => set('business_type', v)}>
              <SelectTrigger data-testid="business-type-select"><SelectValue placeholder="-- Choose --" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sole_proprietor">Sole Proprietor</SelectItem>
                <SelectItem value="llc">LLC</SelectItem>
                <SelectItem value="partnership">Partnership</SelectItem>
                <SelectItem value="corporation">Corporation</SelectItem>
                <SelectItem value="s_corporation">S Corporation</SelectItem>
                <SelectItem value="c_corporation">C Corporation</SelectItem>
                <SelectItem value="nonprofit">Non-Profit</SelectItem>
                <SelectItem value="trust">Trust</SelectItem>
                <SelectItem value="government">Government</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* DBA Toggle */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/10">
          <div>
            <Label className="mb-2 block font-medium">Is your DBA (Doing Business As) information same as Corporate/Legal? *</Label>
            <div className="flex gap-6">
              {['yes', 'no'].map(val => (
                <label key={val} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="dba_same_as_corporate"
                    value={val}
                    checked={formData.dba_same_as_corporate === val}
                    onChange={() => set('dba_same_as_corporate', val)}
                    className="w-4 h-4 accent-[#3b82f6]"
                    data-testid={`dba-same-${val}`}
                  />
                  <span className="text-sm capitalize">{val === 'yes' ? 'Yes' : 'No'}</span>
                </label>
              ))}
            </div>
          </div>

          {formData.dba_same_as_corporate === 'no' && (
            <div className="space-y-3 pt-1 border-t">
              <p className="text-sm font-semibold text-muted-foreground">DBA (Doing Business As) information</p>
              <div>
                <Label className="mb-1.5 block text-sm">DBA Name</Label>
                <Input value={formData.dba_name} onChange={(e) => set('dba_name', e.target.value)}
                  placeholder="Doing Business As name" data-testid="dba-name-input" />
              </div>
              <div>
                <Label className="mb-1.5 block text-sm">Street address of DBA</Label>
                <Input value={formData.dba_address} onChange={(e) => set('dba_address', e.target.value)}
                  placeholder="DBA street address" data-testid="dba-address-input" />
              </div>
              <div className="grid gap-2 md:grid-cols-3">
                <Select value={formData.dba_state} onValueChange={(v) => set('dba_state', v)}>
                  <SelectTrigger data-testid="dba-state-select"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={formData.dba_zip} onChange={(e) => set('dba_zip', e.target.value)}
                  placeholder="ZIP" data-testid="dba-zip-input" />
                <Input value={formData.dba_city} onChange={(e) => set('dba_city', e.target.value)}
                  placeholder="City" data-testid="dba-city-input" />
              </div>
            </div>
          )}
        </div>

        {/* Row 2: Date Founded + Tax ID */}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-[#3b82f6]" /> When was your business founded? *
            </Label>
            <Input type="date" value={formData.date_established} onChange={(e) => set('date_established', e.target.value)}
              required data-testid="date-established-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <CreditCard className="h-3.5 w-3.5 text-[#3b82f6]" /> Federal Tax ID / EIN *
            </Label>
            <Input value={formData.federal_tax_id} onChange={(e) => set('federal_tax_id', e.target.value)}
              placeholder="XX-XXXXXXX" required data-testid="federal-tax-id-input" />
          </div>
        </div>

        {/* Row 3: Phone + Email */}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Phone className="h-3.5 w-3.5 text-[#3b82f6]" /> Business Phone *
            </Label>
            <Input value={formData.business_phone} onChange={(e) => set('business_phone', e.target.value)}
              placeholder="(555) 123-4567" required data-testid="business-phone-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Mail className="h-3.5 w-3.5 text-[#3b82f6]" /> Business Email *
            </Label>
            <Input type="email" value={formData.business_email} onChange={(e) => set('business_email', e.target.value)}
              placeholder="business@example.com" required data-testid="business-email-input" />
          </div>
        </div>

        {/* Address Block */}
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5">
            <MapPin className="h-3.5 w-3.5 text-[#3b82f6]" /> Street Address *
          </Label>
          <AddressAutocomplete
            value={formData.business_address}
            onChange={(val) => set('business_address', val)}
            onPlaceSelected={(place) => {
              setFormData(prev => ({
                ...prev,
                business_address: place.street,
                business_city: place.city,
                business_state: place.state,
                business_zip: place.zip,
              }));
            }}
            placeholder="Start typing a business address..."
            className="mb-2"
            data-testid="business-address-input"
          />
          <div className="grid gap-2 md:grid-cols-4">
            <Select value={formData.business_state} onValueChange={(v) => set('business_state', v)}>
              <SelectTrigger data-testid="business-state-select"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={formData.business_zip} onChange={(e) => set('business_zip', e.target.value)}
              placeholder="ZIP" data-testid="business-zip-input" />
            <Input value={formData.business_city} onChange={(e) => set('business_city', e.target.value)}
              placeholder="City" data-testid="business-city-input" />
            <Input value={formData.business_suite} onChange={(e) => set('business_suite', e.target.value)}
              placeholder="Suite/Apt#" data-testid="business-suite-input" />
          </div>
        </div>

        {/* Business Location */}
        <div className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Home className="h-4 w-4 text-[#3b82f6]" />
            <span className="font-semibold text-sm">Business location</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <RadioGroup
              label="Building Type"
              required
              testIdPrefix="building-type"
              value={formData.building_type}
              onChange={(v) => set('building_type', v)}
              options={[
                { value: 'Shopping Center', label: 'Shopping Center' },
                { value: 'Office Building', label: 'Office Building' },
                { value: 'Industrial Building', label: 'Industrial Building' },
                { value: 'Residence', label: 'Residence' },
              ]}
            />
            <RadioGroup
              label="Building Ownership"
              required
              testIdPrefix="building-ownership"
              value={formData.building_ownership}
              onChange={(v) => set('building_ownership', v)}
              options={[
                { value: 'Owns', label: 'Owns' },
                { value: 'Rents', label: 'Rents' },
              ]}
            />
            <RadioGroup
              label="Area Zoned"
              required
              testIdPrefix="area-zoned"
              value={formData.area_zoned}
              onChange={(v) => set('area_zoned', v)}
              options={[
                { value: 'Commercial', label: 'Commercial' },
                { value: 'Industrial', label: 'Industrial' },
                { value: 'Residential', label: 'Residential' },
              ]}
            />
            <RadioGroup
              label="Square Footage"
              required
              testIdPrefix="square-footage"
              value={formData.square_footage}
              onChange={(v) => set('square_footage', v)}
              options={[
                { value: '0-500', label: '0-500' },
                { value: '501-2500', label: '501-2500' },
                { value: '2501-5000', label: '2501-5000' },
                { value: '5001-10000', label: '5001-10000' },
                { value: '10000+', label: '10000+' },
              ]}
            />
          </div>
        </div>

        {/* Website + MCC + Goods/Services + Keyed */}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Globe className="h-3.5 w-3.5 text-[#3b82f6]" /> Website
            </Label>
            <Input value={formData.website} onChange={(e) => set('website', e.target.value)}
              placeholder="https://example.com" data-testid="website-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 mb-1.5">
              <Tag className="h-3.5 w-3.5 text-[#3b82f6]" /> MCC Code *
            </Label>
            <Input value={formData.mcc_code} onChange={(e) => set('mcc_code', e.target.value)}
              placeholder="e.g. 5812" required data-testid="mcc-code-input" />
          </div>
        </div>

        {/* Goods/Services + Keyed % */}
        <div>
          <Label className="flex items-center gap-1.5 mb-1.5">
            <ShoppingCart className="h-3.5 w-3.5 text-[#3b82f6]" /> Goods/Services Sold *
          </Label>
          <Textarea value={formData.goods_services_sold} onChange={(e) => set('goods_services_sold', e.target.value)}
            placeholder="Describe what your business sells or services provided" rows={3} required data-testid="goods-services-input" />
        </div>

        <div>
          <Label className="flex items-center gap-1.5 mb-1.5">
            <Keyboard className="h-3.5 w-3.5 text-[#3b82f6]" /> % Keyed Transactions *
          </Label>
          <div className="relative max-w-[200px]">
            <Input type="number" min="0" max="100" value={formData.keyed_transactions_pct}
              onChange={(e) => set('keyed_transactions_pct', e.target.value)} placeholder="0" required
              className="pr-8" data-testid="keyed-transactions-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.(formData)} data-testid="save-draft-btn">
          <Save className="h-4 w-4 mr-2" /> Exit & Save Draft
        </Button>
        <Button type="submit" disabled={saving} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="save-continue-btn">
          {saving ? 'Saving...' : 'Save & Continue \u2192'}
        </Button>
      </div>
    </form>
  );
}

export default Step1BusinessInfo;
